import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Trash2, TestTube2, Loader2 } from "lucide-react";

const ROLE_LABEL = { passenger: "Passager", driver: "Chauffeur", restaurateur: "Restaurateur" };
const ZONE_FLAG = { africa: "🇨🇲", europe: "🇪🇺" };

export default function SeedTestsCard() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // email OR "__all__"

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/seed-tests");
      setAccounts(data.accounts || []);
    } catch (e) {
      toast.error("Impossible de charger les comptes de test.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const deleteOne = async (email) => {
    if (!window.confirm(`Supprimer définitivement le compte de test ${email} ?`)) return;
    setBusy(email);
    try {
      await api.delete(`/admin/seed-tests/${encodeURIComponent(email)}`);
      toast.success(`✅ ${email} supprimé`);
      load();
    } catch (e) {
      toast.error("Suppression échouée.");
    } finally {
      setBusy(null);
    }
  };

  const purgeAll = async () => {
    const existing = accounts.filter((a) => a.exists);
    if (existing.length === 0) return;
    if (!window.confirm(`Supprimer les ${existing.length} comptes de test présents ? Cette action est irréversible.`)) return;
    setBusy("__all__");
    try {
      const { data } = await api.post("/admin/seed-tests/purge-all");
      const deleted = (data.results || []).filter((r) => r.deleted).length;
      toast.success(`✅ ${deleted} compte(s) de test supprimé(s)`);
      load();
    } catch (e) {
      toast.error("Purge échouée.");
    } finally {
      setBusy(null);
    }
  };

  const existingCount = accounts.filter((a) => a.exists).length;

  return (
    <div data-testid="seed-tests-card" className="mb-6 rounded-2xl border-2 border-dashed border-[#F26419]/40 bg-[#FFF7EF] p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <TestTube2 className="w-5 h-5 text-[#F26419]" />
          <h3 className="font-head font-black text-base text-[#1A1A1A]">Comptes de test seedés</h3>
          <span className="text-xs font-bold text-[#75807A]">({existingCount} présent{existingCount > 1 ? "s" : ""})</span>
        </div>
        {existingCount > 0 && (
          <button
            data-testid="seed-tests-purge-all"
            onClick={purgeAll}
            disabled={busy === "__all__"}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#D94F2B] hover:bg-[#B84424] text-white text-xs font-bold disabled:opacity-60 transition-colors"
          >
            {busy === "__all__" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Tout supprimer
          </button>
        )}
      </div>
      <p className="mt-1.5 text-xs text-[#75807A]">
        Comptes créés automatiquement au démarrage (PIN 1234) pour tester l'app. Supprime-les quand tu as fini.
      </p>

      {loading ? (
        <div className="mt-3 text-xs text-[#75807A] flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…</div>
      ) : (
        <ul className="mt-3 space-y-2">
          {accounts.map((a) => (
            <li
              key={a.email}
              data-testid={`seed-test-${a.email}`}
              className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm ${a.exists ? "bg-white border-[#E2DFD8]" : "bg-[#F0EBE1]/60 border-[#E2DFD8] opacity-60"}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base">{ZONE_FLAG[a.zone] || "🌍"}</span>
                  <span className="font-bold text-[#1A1A1A] truncate">{a.name || a.email}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F26419]/10 text-[#D94F2B]">
                    {ROLE_LABEL[a.role] || a.role || "?"}
                  </span>
                  {!a.exists && <span className="text-[10px] font-bold text-[#75807A]">déjà supprimé</span>}
                </div>
                <div className="text-[11px] text-[#75807A] truncate">{a.email}</div>
              </div>
              {a.exists && (
                <button
                  data-testid={`seed-test-delete-${a.email}`}
                  onClick={() => deleteOne(a.email)}
                  disabled={busy === a.email}
                  title={`Supprimer ${a.email}`}
                  className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full border border-[#D94F2B]/40 text-[#D94F2B] hover:bg-[#D94F2B] hover:text-white text-xs font-bold disabled:opacity-60 transition-colors shrink-0"
                >
                  {busy === a.email ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  Supprimer
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
